import { useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { CHART_TOOLTIP_PROPS, chartAxisProps, chartGridProps } from '@/config/chartTheme'
import { BasisNote } from '@/components/dashboards/KnownFigures'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import {
  OUTCOME_COLOR,
  compactCurrency,
  cumulative,
  drillForIds,
  type FlowPoint,
  type FlowResponse,
} from '@/services/dashboardAnalytics'

type FlowSeries = 'received' | 'signed' | 'funded' | 'dead'

const SERIES_LABEL: Record<FlowSeries, string> = {
  received: 'Received',
  signed: 'Signed',
  funded: 'Funded',
  dead: 'Dead',
}

const SERIES_COLOR: Record<FlowSeries, string> = {
  received: '#8C877D',
  signed: OUTCOME_COLOR.signed,
  funded: OUTCOME_COLOR.funded,
  dead: OUTCOME_COLOR.dead,
}

interface FlowChartProps {
  data: FlowResponse | null | undefined
  /** Which series to draw, in order. */
  series?: FlowSeries[]
  /** Draw the running total of funded volume on a right-hand axis. */
  cumulativeFunded?: boolean
  /** Title prefix for drills — the cohort or the person. */
  scopeLabel?: string
  /** Let the reader flip between deal counts and dollar volume. */
  defaultMetric?: 'count' | 'amount'
  className?: string
}

interface ChartRow {
  month: string
  label: string
  point: FlowPoint
  received: number | null
  signed: number | null
  funded: number | null
  dead: number | null
  cumulativeFunded: number | null
}

/**
 * Monthly flow — deals received against deals signed and funded, each
 * counted in the month of its own date. A bar opens exactly that month's
 * deals for that series. The server's basis line (deals with no date for the
 * chosen field) is printed under the chart, so a quiet month is never read as
 * a missing one.
 */
export default function FlowChart({
  data,
  series = ['received', 'signed', 'funded'],
  cumulativeFunded = true,
  scopeLabel,
  defaultMetric = 'count',
  className,
}: FlowChartProps) {
  const { open } = useDrillDown()
  const [metric, setMetric] = useState<'count' | 'amount'>(defaultMetric)
  const points = useMemo(() => data?.points ?? [], [data])

  const rows = useMemo<ChartRow[]>(() => {
    const pick = (point: FlowPoint, key: FlowSeries) =>
      metric === 'count' ? point[key]?.count ?? 0 : point[key]?.amount ?? null
    const fundedAmounts = points.map((point) => point.funded?.amount ?? null)
    const running = cumulative(fundedAmounts)
    return points.map((point, index) => ({
      month: point.month,
      label: point.label,
      point,
      received: pick(point, 'received'),
      signed: pick(point, 'signed'),
      funded: pick(point, 'funded'),
      dead: pick(point, 'dead'),
      cumulativeFunded: running[index],
    }))
  }, [points, metric])

  // A series with nothing in any month (dead deals are not imported yet) is
  // left out rather than drawn as a flat zero line that reads as "none died".
  const drawn = series.filter((key) => points.some((point) => (point[key]?.count ?? 0) > 0))
  const showCumulative = cumulativeFunded && rows.some((row) => row.cumulativeFunded != null)

  const onBar = (key: FlowSeries) => (entry: { payload?: ChartRow }) => {
    const point = entry?.payload?.point
    if (!point) return
    const cell = point[key]
    const prefix = scopeLabel ? `${scopeLabel} — ` : ''
    open(drillForIds(cell?.dealIds, `${prefix}${SERIES_LABEL[key]} in ${point.label}`))
  }

  const tick = (value: number) => (metric === 'count' ? value.toLocaleString('en-US') : compactCurrency(value))

  if (points.length === 0) {
    return (
      <p className="px-1 py-3 text-[11px] text-stone-500">
        No month on record for this cohort{data?.basis ? ` — ${data.basis}` : '.'}
      </p>
    )
  }

  return (
    <div className={clsx('flex min-h-0 flex-1 flex-col gap-1', className)}>
      <div className="flex flex-shrink-0 items-center justify-end gap-1">
        {(['count', 'amount'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setMetric(key)}
            aria-pressed={metric === key}
            className={clsx(
              'rounded-full border px-2 py-0.5 text-[9.5px] transition-colors',
              metric === key
                ? 'border-amber-400/35 bg-amber-400/[0.08] text-amber-200'
                : 'border-amber-400/10 text-stone-500 hover:text-stone-300',
            )}
          >
            {key === 'count' ? 'Deals' : 'Volume'}
          </button>
        ))}
      </div>
      <div className="min-h-[140px] flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 6, right: showCumulative ? 4 : 8, left: -6, bottom: 0 }} barGap={1}>
            <CartesianGrid {...chartGridProps()} />
            <XAxis dataKey="label" {...chartAxisProps(9)} interval="preserveStartEnd" minTickGap={12} />
            <YAxis yAxisId="left" {...chartAxisProps(9)} tickFormatter={tick} width={metric === 'count' ? 30 : 52} allowDecimals={false} />
            {showCumulative && (
              <YAxis
                yAxisId="right"
                orientation="right"
                {...chartAxisProps(9)}
                tickFormatter={(value: number) => compactCurrency(value)}
                width={52}
              />
            )}
            <Tooltip
              {...CHART_TOOLTIP_PROPS}
              cursor={{ fill: 'rgba(217,183,104,0.05)' }}
              formatter={(value: number, name: string) => [
                name === 'Cumulative funded' ? compactCurrency(value) : tick(value),
                name,
              ]}
            />
            <Legend
              iconSize={8}
              wrapperStyle={{ fontSize: 9.5, color: '#a2a2a2', paddingTop: 2 }}
            />
            {drawn.map((key) => (
              <Bar
                key={key}
                yAxisId="left"
                dataKey={key}
                name={SERIES_LABEL[key]}
                fill={SERIES_COLOR[key]}
                radius={[2, 2, 0, 0]}
                maxBarSize={14}
                cursor="pointer"
                onClick={onBar(key)}
              />
            ))}
            {showCumulative && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cumulativeFunded"
                name="Cumulative funded"
                stroke="#EDE6D6"
                strokeWidth={1.6}
                dot={false}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {data?.basis && (
        <p className="flex-shrink-0 text-[9.5px] text-stone-500">
          <BasisNote full={data.basis}>
            {data.coverage?.received?.undated
              ? `Each bar sits in the month of its own date; ${data.coverage.received.undated} deals carry no received date and are not placed.`
              : 'Each bar sits in the month of its own date.'}
          </BasisNote>
        </p>
      )}
    </div>
  )
}
